/**
 * Live OSM hydrant + static-water lookup. Unlike the seeded Bungendore
 * fixture (data/localities/bungendore.json, synthetic stand-in for the demo
 * only), this queries the real Overpass API for the incident's actual
 * coordinates — so Water Plan derivation works for any address, not just the
 * one scripted locality. Fails soft: any network/parse problem returns
 * `undefined` and the caller leaves the Water Plan fields unset ("unknown"),
 * never fabricating a value.
 */
import type { LngLat, SeededFeature } from '@duty/shared';

// Overpass mirrors, tried in order — overpass-api.de itself sometimes
// 406s/rate-limits from shared egress IPs; the other two are more reliable.
const OVERPASS_ENDPOINTS = [
  'https://z.overpass-api.de/api/interpreter',
  'https://maps.mail.ru/osm/tools/overpass/api/interpreter',
  'https://overpass-api.de/api/interpreter',
];

const QUERY_RADIUS_M = 2000;
const FETCH_TIMEOUT_MS = 8000;
const ATTRIBUTION = '© OpenStreetMap contributors (ODbL), via Overpass API';

interface OverpassElement {
  type: 'node' | 'way' | 'relation';
  id: number;
  lat?: number;
  lon?: number;
  center?: { lat: number; lon: number };
  tags?: Record<string, string>;
}

interface OverpassResponse {
  elements: OverpassElement[];
}

function buildQuery(point: LngLat, radiusM: number): string {
  const around = `around:${radiusM},${point.lat},${point.lng}`;
  return (
    `[out:json][timeout:10];(` +
    `node["emergency"="fire_hydrant"](${around});` +
    `node["natural"="water"](${around});way["natural"="water"](${around});` +
    `node["landuse"="reservoir"](${around});way["landuse"="reservoir"](${around});` +
    `node["man_made"="water_tank"](${around});` +
    `node["man_made"="reservoir_covered"](${around});way["man_made"="reservoir_covered"](${around});` +
    `);out center;`
  );
}

function elementCoord(el: OverpassElement): LngLat | undefined {
  if (el.lat !== undefined && el.lon !== undefined) return { lat: el.lat, lng: el.lon };
  if (el.center) return { lat: el.center.lat, lng: el.center.lon };
  return undefined;
}

function labelFor(el: OverpassElement, kind: 'hydrant' | 'static-water'): string {
  const name = el.tags?.name;
  if (name) return name;
  if (kind === 'hydrant') return 'Fire hydrant (OSM)';
  if (el.tags?.landuse === 'reservoir') return 'Reservoir (OSM)';
  if (el.tags?.man_made === 'water_tank') return 'Water tank (OSM)';
  if (el.tags?.man_made === 'reservoir_covered') return 'Covered reservoir (OSM)';
  return 'Static water (OSM)';
}

export interface OsmWaterFetchResult {
  features: SeededFeature[];
  source: string;
}

/**
 * Live OSM lookup for hydrants + static water near a point. Prefers the API's
 * `/api/water-lookup` endpoint first — it's backed by a shared, ~1yr-TTL
 * cache (see api/src/functions/water-lookup.ts), so repeated lookups near the
 * same area don't hammer the shared Overpass mirrors for every user. Falls
 * back to querying Overpass directly from the browser when the API is
 * unreachable (standalone/local mode with no backend, or a network hiccup) —
 * never throws; returns `undefined` if every path fails, which the caller
 * must treat as "unknown", not as an error to surface loudly.
 */
export async function fetchOsmWaterFeatures(point: LngLat): Promise<OsmWaterFetchResult | undefined> {
  const viaApi = await fetchViaApi(point);
  if (viaApi) return viaApi;
  return fetchViaOverpass(point);
}

async function fetchViaApi(point: LngLat): Promise<OsmWaterFetchResult | undefined> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(`/api/water-lookup?lng=${point.lng}&lat=${point.lat}`, { signal: controller.signal });
    if (!res.ok) return undefined;
    const data = (await res.json()) as { features: SeededFeature[]; source: string };
    return { features: data.features, source: data.source };
  } catch {
    return undefined; // API absent (standalone mode) or unreachable — fall back to a direct Overpass fetch.
  } finally {
    clearTimeout(timeout);
  }
}

async function fetchViaOverpass(point: LngLat): Promise<OsmWaterFetchResult | undefined> {
  const query = buildQuery(point, QUERY_RADIUS_M);
  for (const endpoint of OVERPASS_ENDPOINTS) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        body: `data=${encodeURIComponent(query)}`,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        signal: controller.signal,
      });
      if (!res.ok) continue;
      const data = (await res.json()) as OverpassResponse;
      const now = new Date().toISOString();
      const features: SeededFeature[] = [];
      for (const el of data.elements) {
        const coord = elementCoord(el);
        if (!coord) continue;
        const kind: SeededFeature['kind'] = el.tags?.emergency === 'fire_hydrant' ? 'hydrant' : 'static-water';
        features.push({
          id: `osm-${el.type}-${el.id}`,
          kind,
          coord,
          label: labelFor(el, kind),
          provenance: 'seeded-osm',
          source: ATTRIBUTION,
          capturedAt: now,
        });
      }
      return { features, source: ATTRIBUTION };
    } catch {
      // Network error, abort, or bad JSON — try the next mirror.
    } finally {
      clearTimeout(timeout);
    }
  }
  return undefined;
}
